import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Présence commerciale";
const h1Text = "Votre partenaire stratégique pour une forte présence commerciale locale dans la région DACH. Avec une représentation et une adresse à Duesseldorf, nous positionnons votre entreprise de TIC de manière excellente, sécurisée et rentable sur le marché.";
const heroImage = "https://net-basis.net/Bilder/duesseldorf-media-harbor-rhine-tower-business-location.webp";
const heroAlt = "Le Medienhafen de Düsseldorf et la tour du Rhin évoquent une présence locale sur le marché DACH.";
const heroClaimLine1 = "Pôle économique";
const heroClaimLine2 = "de Düsseldorf";
const benefitsTitle = "Comment vous profitez de notre expertise en __présence commerciale__ :";
const p1 = "Pour de nombreuses entreprises, l'entrée sur de nouveaux marchés ainsi que leur développement réussi sont associés à d'efforts importants. Les sensibilités, les points de vue divergents ou les attentes des partenaires commerciaux potentiels sont régulièrement sous-estimés. Ces derniers préfèrent généralement disposer d'un interlocuteur local qui parle leur langue et qui connaît bien leurs habitudes de vie, le droit de leur pays et leurs pratiques commerciales. Le manque de connaissances du marché, du pays et de la langue, ainsi que des ressources en personnel ou financières insuffisantes, constituent donc des obstacles considérables.";
const p2Text = "C'est précisément là que NetBasis International aide avec son service de « présence commerciale » en tant que passerelle entre votre entreprise et les nouveaux marchés, pour surmonter efficacement toutes les difficultés ou obstacles potentiels.";
const p2Parts = p2Text.split(COMPANY_NAME);
const p3 = "Avec une présence commerciale locale et une adresse à Düsseldorf – qui non seulement met en avant le nom de votre entreprise ou vous fournit un numéro de téléphone et un interlocuteur local, mais gère également l'intégralité de vos activités en votre nom – vous êtes excellemment et de manière rentable positionné.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Minimisation rigoureuse des coûts, de la charge opérationnelle, du temps et des risques",
  "Aucune nécessité de disposer d'une présence juridique ou d'une entité propre sur place",
  "Opportunités d'entrée immédiate et flexible sur le marché",
  "Occasion favorable de réaliser un test de marché et de produit approfondi",
  "Aucun personnel propre ni infrastructure administrative requis",
  "Suivi plus efficace des contacts commerciaux déjà existants",
  "Soulagement durable de votre propre service d'exportation",
  "Élimination totale des barrières linguistiques grâce à une communication directe et simple",
  "Accès immédiat aux bases de données, listes d'adresses et intégration dans les réseaux",
  "Entrée optimale ou transition transparente vers votre propre succursale locale",
];

export default function PresenceCommercialeFrPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Présence Commerciale | NetBasis International"
        description="Votre succursale locale sur le marché DACH sans contraintes administratives – professionnelle, présente et immédiatement opérationnelle."
        ogUrl="https://net-basis.net/fr/services/developpement-commercial/presence-commerciale"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[22%] md:pt-[10%] lg:pt-[12%] pl-[8%] md:pl-[10%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[trainingTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="block">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 text-sm md:text-base mb-4">
              {h1Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>{p3}</p>
            </div>

            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}